<template>
  <div class="relative">
    <div
      class="hidden tablet-h:block relative z-100 bg-white border-b border-[#F3F3F3] tablet-h:border-[#E5E5E5]"
    >
      <div
        class="h-[50px] tablet-h:h-[60px] px-3 tablet-h:px-5 py-1.5 tablet-h:py-3 flex items-center tablet-h:justify-center relative"
      >
        <div
          class="h-full tablet-h:absolute left-3 tablet-h:left-5 flex items-center"
        >
          <RouterLink
            v-if="origin"
            class="hidden tablet-h:block tablet-h:h-[30px]"
            to="/home"
          >
            <img
              v-if="origin.id !== 'Dacr'"
              :src="origin.logo.black.url"
              class="h-full"
            />
            <DacrWordLogo v-else class="h-full" />
          </RouterLink>
        </div>
        <Search class="w-full tablet-h:min-w-[580px] tablet-h:max-w-[580px]" />
        <div
          class="hidden tablet-h:flex items-center justify-end absolute right-3 tablet-h:right-5"
        >
          <a
            href="/download"
            class="hidden font-primary font-medium text-[14px] leading-[25px] text-gray-800 items-center gap-[8px] rounded-[6px] pt-[2px] pr-[10px] pb-[3px] pl-[3px] hover:bg-gray-200/25 transition-colors duration-250 mr-[14px] cursor-pointer"
            :class="downloadClasses"
            target="_blank"
          >
            <div
              class="w-6 h-6 shrink-0 flex items-center justify-center rounded-[7px] border border-gray-400/25 bg-white"
            >
              <DacrMarkIcon class="size-[17px] shrink-0" />
            </div>
            <div class="font-primary">Get app</div>
          </a>

          <div ref="notificationsRoot" class="relative mr-6 flex">
            <div
              class="w-9 h-9 -m-[5px] flex justify-center items-center rounded-full hover:bg-gray-200/25 transition-colors duration-250 cursor-pointer"
              @click="showNotifications = !showNotifications"
            >
              <div
                class="w-[26px] h-[26px] flex justify-center items-center relative"
              >
                <BellIcon class="size-full text-gray-800" />
                <div
                  v-if="showNotificationBadge"
                  class="w-[13px] h-[13px] border-[1.5px] border-white absolute -top-1 -right-[2px] bg-red-700 rounded-full"
                />
              </div>
            </div>
            <Transition name="popover-down">
              <Notifications
                v-if="showNotifications"
                class="absolute right-0 top-[calc(100%+3px)] z-50"
              />
            </Transition>
          </div>
          <DesktopAccountMenu />
          <Button
            v-if="user && isBasicUser"
            class="ml-[22px]"
            variant="primary"
            @click="handleUpgradeClick"
          >
            Upgrade
          </Button>
        </div>
      </div>
    </div>

    <MobileTopNav
      v-if="showMobileTopNav"
      class="tablet-h:hidden"
      @account="handleAccountClick"
    />
    <MobileBottomNav
      v-if="showMobileBottomNav"
      class="tablet-h:hidden"
      :notifications-open="showMobileNotification"
      :has-unread="showNotificationBadge"
      @activity="handleActivityClick"
      @navigate="closeMobileMenu"
    />

    <Notifications
      v-if="showMobileNotification"
      class="tablet-h:hidden fixed inset-x-0 top-0 z-40"
    />

    <MobileMenu v-model:open="showMobileMenu" />
  </div>
</template>

<script setup lang="ts">
import DacrWordLogo from '@/assets/svg/other/DacrWordLogo.svg'
import Search from '@/features/search/components/SearchBar.vue'
import { Button } from '@dacr/library'
import { BellIcon, DacrMarkIcon } from '@dacr/icons'
import Notifications from '@/shared/components/Notifications.vue'
import {
  useNotificationsQuery,
  UserPermission,
} from '@/shared/graphql/legacy/generated'
import { useRoute, useRouter } from 'vue-router'
import DesktopAccountMenu from '@/shared/components/account-menu/DesktopAccountMenu.vue'
import { computed, ref, watch } from 'vue'
import MobileMenu from '@/shared/components/account-menu/MobileMenu.vue'
import MobileTopNav from '@/shared/components/MobileTopNav.vue'
import MobileBottomNav from '@/shared/components/MobileBottomNav.vue'
import { useMobileSearchStore } from '@/features/search/store'
import { useUserStore } from '@/shared/stores/user'
import { useEventListener } from '@/shared/composables/useEventListener'
import { unreadBadgeCount } from '@/shared/composables/useNotificationsRead'
import { openModal, Modals } from '@/shared/dialogs/modals'
import { useOriginStore } from '@/shared/stores/origin'

const { result } = useNotificationsQuery(
  {},
  {
    pollInterval: 60_000,
  }
)

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const originStore = useOriginStore()
const mobileSearchStore = useMobileSearchStore()

const isSettings = computed(() => route.path.startsWith('/settings'))
const showMobileTopNav = computed(() => !isSettings.value)
const showMobileBottomNav = computed(
  () => !mobileSearchStore.isActive && !isSettings.value
)

const showMobileNotification = computed(() => route.query.activity === '1')
const showMobileMenu = ref(false)
const showNotifications = ref(false)
const notificationsRoot = ref<HTMLElement>()

useEventListener(window, 'click', (event) => {
  if (!notificationsRoot.value?.contains(event.target as Node)) {
    showNotifications.value = false
  }
})

const showNotificationBadge = computed(
  () => unreadBadgeCount(result.value?.notifications) > 0
)

const user = computed(() => userStore.user)

const origin = computed(() => originStore.userOrigin)

const isBasicUser = computed(
  () => !user.value?.permissions.includes(UserPermission.Pro)
)

const downloadClasses = computed(() => ({
  'min-[1280px]:flex': isBasicUser.value,
  'desktop:flex': !isBasicUser.value,
}))

const closeMobileMenu = () => {
  showMobileMenu.value = false
}

const handleActivityClick = () => {
  closeMobileMenu()
  router.push({ query: { ...route.query, activity: '1' } })
}

const handleAccountClick = () => {
  showMobileMenu.value = true
}

const handleUpgradeClick = () => {
  openModal(Modals.UpgradeToPro)
}

watch(
  () => route.path,
  () => {
    showMobileMenu.value = false
    showNotifications.value = false
  }
)
</script>
